import { memo, useEffect, useState } from 'react'
import { Box, Checkbox, Divider, Grid, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CachedIcon from '@mui/icons-material/Cached'
import RefreshIcon from '@mui/icons-material/Refresh'
import CircularProgress from '@mui/material/CircularProgress'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import {
  useReextractProductAttributePart,
  useReextractProductAttributes,
  useUpdateProductAttributes,
} from 'hooks/useProduct'
import { AttributePart } from 'services/productService'
import { snackbarService } from 'services/snackbarService'

interface NamePair {
  en: string;
  ar: string;
}

interface ColorValue {
  family: NamePair;
  shade: NamePair;
  code: string;
  undertone: NamePair;
}

interface AttributesValue {
  lists: Record<ListKey, NamePair[]>;
  colors: ColorValue[];
  scent: { family: NamePair; name: NamePair };
  flavor: { family: NamePair; name: NamePair };
}

type ListKey =
  | 'hairTypes'
  | 'skinTypes'
  | 'scalpTypes'
  | 'beardTypes'
  | 'usageTimings'
  | 'concerns'
  | 'goals'
  | 'formulaClaims';

// Where each list sits in the stored shape, and what to call it on screen. One
// table rather than nine near-identical blocks: they are all the same thing -
// a name in two languages - and only their place in the object differs.
const LISTS: { key: ListKey; label: string; part: AttributePart; path: string[] }[] = [
  { key: 'hairTypes', label: 'Hair Types', part: 'types', path: ['suitability', 'hairTypes'] },
  { key: 'skinTypes', label: 'Skin Types', part: 'types', path: ['suitability', 'skinTypes'] },
  { key: 'scalpTypes', label: 'Scalp Types', part: 'types', path: ['suitability', 'scalpTypes'] },
  { key: 'beardTypes', label: 'Beard Types', part: 'types', path: ['suitability', 'beardTypes'] },
  { key: 'usageTimings', label: 'Usage Timings', part: 'timings', path: ['suitability', 'usageTimings'] },
  { key: 'concerns', label: 'Concerns', part: 'concerns', path: ['claims', 'concerns'] },
  { key: 'goals', label: 'Goals', part: 'goals', path: ['claims', 'goals'] },
  { key: 'formulaClaims', label: 'Formula Claims', part: 'formula-claims', path: ['formulaClaims'] },
];

const emptyPair = (): NamePair => ({ en: '', ar: '' });

const readPair = (value: any): NamePair => ({ en: value?.en ?? '', ar: value?.ar ?? '' });

const readPairs = (value: any): NamePair[] =>
  Array.isArray(value) ? value.map(readPair) : [];

const at = (source: any, path: string[]) =>
  path.reduce((value, key) => (value == null ? value : value[key]), source);

// A pair with nothing on either side is one nobody filled in; a pair with one
// side is kept, because the missing half is a gap somebody can see and fix.
const writtenPairs = (pairs: NamePair[]) =>
  pairs
    .filter((pair) => pair.en.trim() || pair.ar.trim())
    .map((pair) => ({ en: pair.en.trim(), ar: pair.ar.trim() }));

const writtenPairOrNull = (pair: NamePair) =>
  pair.en.trim() || pair.ar.trim() ? { en: pair.en.trim(), ar: pair.ar.trim() } : null;

const fromProduct = (attributes: any): AttributesValue => ({
  lists: LISTS.reduce(
    (lists, list) => ({ ...lists, [list.key]: readPairs(at(attributes, list.path)) }),
    {} as Record<ListKey, NamePair[]>,
  ),
  colors: Array.isArray(attributes?.colors)
    ? attributes.colors.map((color: any) => ({
      family: readPair(color?.family),
      shade: readPair(color?.shade),
      code: color?.code ?? '',
      undertone: readPair(color?.undertone),
    }))
    : [],
  scent: {
    family: readPair(attributes?.scent?.family),
    name: readPair(attributes?.scent?.name),
  },
  flavor: {
    family: readPair(attributes?.flavor?.family),
    name: readPair(attributes?.flavor?.name),
  },
});

const toProduct = (value: AttributesValue) => {
  const colors = value.colors
    .filter((color) => color.family.en.trim() || color.shade.en.trim() || color.code.trim())
    .map((color) => ({
      family: writtenPairOrNull(color.family),
      shade: writtenPairOrNull(color.shade),
      code: color.code.trim() ? color.code.trim() : null,
      undertone: writtenPairOrNull(color.undertone),
    }));

  const scentFamily = writtenPairOrNull(value.scent.family);
  const scentName = writtenPairOrNull(value.scent.name);
  const flavorFamily = writtenPairOrNull(value.flavor.family);
  const flavorName = writtenPairOrNull(value.flavor.name);

  return {
    suitability: {
      hairTypes: writtenPairs(value.lists.hairTypes),
      skinTypes: writtenPairs(value.lists.skinTypes),
      scalpTypes: writtenPairs(value.lists.scalpTypes),
      beardTypes: writtenPairs(value.lists.beardTypes),
      usageTimings: writtenPairs(value.lists.usageTimings),
    },
    claims: {
      concerns: writtenPairs(value.lists.concerns),
      goals: writtenPairs(value.lists.goals),
    },
    formulaClaims: writtenPairs(value.lists.formulaClaims),
    colors,
    // Null is a pack that states no scent, which is the answer for most of them.
    scent: scentFamily || scentName ? { family: scentFamily, name: scentName } : null,
    // Null for every product that is not tasted, which is nearly all of them.
    flavor: flavorFamily || flavorName ? { family: flavorFamily, name: flavorName } : null,
  };
};

// What the pack says about itself, as fields rather than as the object it is
// stored in: nine lists of a name in two languages, the shades where the colour
// is the product, and what it smells of.
// What can be edited on its own. A list is its own section, and so is each of
// the three the colour pass fills - a reviewer correcting a timing has no
// business being handed every shade to look after at the same time.
type SectionKey = ListKey | 'colors' | 'scent' | 'flavor';

function AttributesDetails() {
  // Everything at once, the way the pencil on the title has always done it, or
  // one section on its own from the pencil beside that section's heading.
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingSection, setEditingSection] = useState<SectionKey | null>(null);
  // Whether the next reading of what this pack claims is taken from the
  // description instead of the label. The pass does that by itself for a pack
  // that claims nothing at all; this is for the pack that claims plenty and
  // claims nothing worth keeping, which only the reviewer reading the names can
  // tell. One switch for the whole section, whichever reload is pressed after
  // it.
  const [fromDescription, setFromDescription] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [value, setValue] = useState<AttributesValue>(fromProduct(null));
  // Long and mostly empty on any one product, so it is folded away until asked
  // for - the same as the label text and the ingredient panels.
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    setValue(fromProduct(product.attributes));
  }, [product.attributes]);

  const { mutate: updateMutation, isLoading } = useUpdateProductAttributes(
    () => {
      setIsEditing(false);
      setEditingSection(null);
      snackbarService.showSnackbar('Product attributes has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the attributes', 'error');
    },
  )

  // The pass run again, for when the pass itself was what was wrong. Its answer
  // replaces this whole section, edits made here included.
  const { mutate: rereadMutation, isLoading: isRereading } = useReextractProductAttributes(
    () => {
      snackbarService.showSnackbar('Attributes have been read again - re-sync the product to the graph', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not read the attributes again', 'error');
    },
  )

  // One pass read again rather than all six. A reviewer looking at a wrong list
  // wants that list re-read, not the other five moved around it - and the other
  // five are five more calls they were not asking to pay for.
  const [reloading, setReloading] = useState<AttributePart | null>(null);

  const { mutate: reloadPart } = useReextractProductAttributePart(
    () => {
      setReloading(null);
      snackbarService.showSnackbar('Read again - re-sync the product to the graph', 'success');
      refetchProduct();
    },
    (_err: any) => {
      setReloading(null);
      snackbarService.showSnackbar('Could not read that part again', 'error');
    },
  )

  // The reload beside a heading. The tooltip names what will change rather than
  // the heading it sits on, because one pass answers several headings.
  const reloadButton = (part: AttributePart, note: string) => (
    <Tooltip title={reloading === part ? 'Reading the pack…' : note}>
      <span>
        <IconButton
          size="small"
          disabled={isEditing || isLoading || isRereading || Boolean(reloading)}
          onClick={() => {
            setReloading(part);
            reloadPart({ productId: product.id, part, withDescription: fromDescription });
          }}
        >
          {reloading === part ? (
            <CircularProgress size={14} />
          ) : (
            <RefreshIcon sx={{ fontSize: 16, color: colors.primary }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  const setListField = (key: ListKey, index: number, language: 'en' | 'ar', text: string) =>
    setValue((previous) => ({
      ...previous,
      lists: {
        ...previous.lists,
        [key]: previous.lists[key].map((pair, i) =>
          i === index ? { ...pair, [language]: text } : pair,
        ),
      },
    }));

  const addToList = (key: ListKey) =>
    setValue((previous) => ({
      ...previous,
      lists: { ...previous.lists, [key]: [...previous.lists[key], emptyPair()] },
    }));

  // One entry moved a place up or down its list. The order is the order the
  // product page shows them in, and both languages move together - they are one
  // entry with two halves, and moving one of them would pair every name with
  // somebody else's.
  const moveInList = (key: ListKey, index: number, by: -1 | 1) =>
    setValue((previous) => {
      const list = [...previous.lists[key]];
      const to = index + by;
      if (to < 0 || to >= list.length) return previous;

      [list[index], list[to]] = [list[to], list[index]];

      return { ...previous, lists: { ...previous.lists, [key]: list } };
    });

  const removeFromList = (key: ListKey, index: number) =>
    setValue((previous) => ({
      ...previous,
      lists: { ...previous.lists, [key]: previous.lists[key].filter((_pair, i) => i !== index) },
    }));

  const setColorField = (
    index: number,
    field: 'family' | 'shade' | 'undertone',
    language: 'en' | 'ar',
    text: string,
  ) =>
    setValue((previous) => ({
      ...previous,
      colors: previous.colors.map((color, i) =>
        i === index ? { ...color, [field]: { ...color[field], [language]: text } } : color,
      ),
    }));

  const setColorCode = (index: number, text: string) =>
    setValue((previous) => ({
      ...previous,
      colors: previous.colors.map((color, i) => (i === index ? { ...color, code: text } : color)),
    }));

  const setScent = (field: 'family' | 'name', language: 'en' | 'ar', text: string) =>
    setValue((previous) => ({
      ...previous,
      scent: { ...previous.scent, [field]: { ...previous.scent[field], [language]: text } },
    }));

  const setFlavor = (field: 'family' | 'name', language: 'en' | 'ar', text: string) =>
    setValue((previous) => ({
      ...previous,
      flavor: { ...previous.flavor, [field]: { ...previous.flavor[field], [language]: text } },
    }));

  // A field is open when the whole card is being edited, or when the section it
  // belongs to is the one being edited.
  const editable = (section: SectionKey) => isEditing || editingSection === section;

  const handleSave = () => {
    updateMutation({ id: product.id, attributes: toProduct(value) as any });
  }

  const handleCancel = () => {
    setValue(fromProduct(product.attributes));
    setIsEditing(false);
    setEditingSection(null);
  }

  // The pencil and, while a section is open, its save and cancel - beside the
  // heading rather than at the top of the card, since it is that heading's rows
  // they act on. What is sent is the whole column with this section's rows
  // changed, because that is the shape the endpoint takes; nothing else in it
  // has been touched.
  const sectionButtons = (section: SectionKey) => {
    const open = editingSection === section;

    if (isEditing) return null;

    return open ? (
      <>
        <Tooltip title="Save">
          <span>
            <IconButton size="small" disabled={isLoading} onClick={() => handleSave()}>
              <SaveIcon size={15} color={colors.success} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <span>
            <IconButton size="small" disabled={isLoading} onClick={() => handleCancel()}>
              <XIcon size={12} color={colors.error} />
            </IconButton>
          </span>
        </Tooltip>
      </>
    ) : (
      <Tooltip title="Edit this list">
        <span>
          <IconButton
            size="small"
            disabled={isLoading || isRereading || Boolean(reloading) || Boolean(editingSection)}
            onClick={() => setEditingSection(section)}
          >
            <PencilIcon size={15} color={colors.primary} />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // The bin beside the scent and the flavour. A list drops an entry by the bin
  // on its row; these two are one value spread over four boxes, and emptying
  // them by hand is the same edit made four times. Shown only while the section
  // is open, like the boxes it clears.
  const clearButton = (section: 'scent' | 'flavor') =>
    editable(section) ? (
      <Tooltip title="Clear">
        <span>
          <IconButton
            size="small"
            disabled={isLoading}
            onClick={() =>
              setValue((previous) => ({
                ...previous,
                [section]: { family: { en: '', ar: '' }, name: { en: '', ar: '' } },
              }))
            }
          >
            <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
          </IconButton>
        </span>
      </Tooltip>
    ) : null;

  // Everything answered for, across all of it: the eight lists, the shades, and
  // the scent. One number rather than eight, because the question the title is
  // answering is only whether there is anything behind the eye.
  const answered =
    LISTS.reduce(
      (total, list) =>
        total + value.lists[list.key].filter((pair) => pair.en.trim() || pair.ar.trim()).length,
      0,
    ) +
    value.colors.filter(
      (color) => color.family.en.trim() || color.shade.en.trim() || color.code.trim(),
    ).length +
    (value.scent.family.en.trim() || value.scent.name.en.trim() ? 1 : 0) +
    (value.flavor.family.en.trim() || value.flavor.name.en.trim() ? 1 : 0);

  const pairFields = (
    pair: NamePair,
    onChange: (language: 'en' | 'ar', text: string) => void,
    labels: { en: string; ar: string },
    section: SectionKey,
  ) => (
    <Grid container spacing={1.5}>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomLabel label={labels.en} />
        <TextField
          size="small"
          fullWidth
          value={pair.en}
          disabled={!editable(section)}
          onChange={(event) => onChange('en', event.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <CustomLabel label={labels.ar} />
        <TextField
          size="small"
          fullWidth
          value={pair.ar}
          disabled={!editable(section)}
          onChange={(event) => onChange('ar', event.target.value)}
          sx={{ '& input': { direction: 'rtl' } }}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box mt={3}>
      <Section
        title="Product Attributes"
        subtitle={answered ? `${answered} set` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <CachedIcon sx={{ fontSize: 20, color: colors.primary }} />,
                title: 'Read the attributes again',
                onClick: () => rereadMutation({ productId: product.id, withDescription: fromDescription }),
                loading: isRereading,
                disabled: isLoading || Boolean(editingSection),
              },
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isLoading || isRereading || Boolean(editingSection),
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing || Boolean(editingSection) ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {/* Above the lists rather than among the buttons in the heading: it
                is read with the names it would change, and it applies to
                whatever is pressed next - the whole pass, or one list on its
                own. */}
            <Stack
              direction="row"
              alignItems="center"
              mb={1.5}
              onClick={() => {
                if (isLoading || editingSection) return;
                setFromDescription((from) => !from);
              }}
              sx={{
                cursor: isLoading || editingSection ? 'default' : 'pointer',
                userSelect: 'none',
                width: 'fit-content',
              }}
            >
              <Checkbox
                size="small"
                checked={fromDescription}
                disabled={isLoading || Boolean(editingSection)}
                onChange={(event) => setFromDescription(event.target.checked)}
                sx={{ p: 0.5 }}
              />
              <CustomText text="With Description" fontSize={13} color={colors.gray} />
            </Stack>

            <Stack gap={2.5}>
              {LISTS.map((list) => {
                const pairs = value.lists[list.key];

                // An empty list is nothing to read, so outside editing it is not
                // a heading with a blank under it.
                if (!pairs.length && !editable(list.key)) return null;

                return (
                  <Box
                    key={list.key}
                    sx={{
                      border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <CustomText text={list.label} fontWeight={600} />
                      {reloadButton(list.part, 'Read this off the pack again')}
                      {sectionButtons(list.key)}
                    </Stack>

                    <Stack gap={1.5} mt={1.5}>
                      {pairs.map((pair, index) => (
                        <Stack key={index} direction="row" alignItems="flex-start" gap={1}>
                          <Box flex={1}>
                            {pairFields(
                              pair,
                              (language, text) => setListField(list.key, index, language, text),
                              { en: 'English', ar: 'Arabic' },
                              list.key,
                            )}
                          </Box>

                          {editable(list.key) && (
                            <Box sx={{ pt: 2.75 }}>
                              {/* Disabled at the ends rather than hidden, so the
                                  controls stay in the same places down the
                                  list. */}
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === 0}
                                    onClick={() => moveInList(list.key, index, -1)}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === value.lists[list.key].length - 1}
                                    onClick={() => moveInList(list.key, index, 1)}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeFromList(list.key, index)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Stack>
                      ))}

                      {editable(list.key) && (
                        <Box>
                          <CustomButton
                            text={'Add'}
                            variant={'outlined'}
                            startIcon={<AddIcon />}
                            height={38}
                            onClick={() => addToList(list.key)}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}

              {(value.colors.length > 0 || editable('colors')) && (
                <Box
                  sx={{
                    border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <CustomText text={'Colors'} fontWeight={600} />
                    {reloadButton('appearance', 'Read the colour, scent and flavour off the pack again')}
                    {sectionButtons('colors')}
                  </Stack>

                  <Stack gap={2} mt={1.5}>
                    {value.colors.map((color, index) => (
                      <Box key={index}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <CustomText
                            text={`Color ${index + 1}`}
                            fontSize={14}
                            color={colors.gray}
                          />

                          {editable('colors') && (
                            <Tooltip title="Remove this color">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setValue((previous) => ({
                                    ...previous,
                                    colors: previous.colors.filter((_item, i) => i !== index),
                                  }))
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>

                        <Stack gap={1.25}>
                          {pairFields(
                            color.family,
                            (language, text) => setColorField(index, 'family', language, text),
                            { en: 'Family (English)', ar: 'Family (Arabic)' },
                            'colors',
                          )}

                          {pairFields(
                            color.shade,
                            (language, text) => setColorField(index, 'shade', language, text),
                            { en: 'Shade (English)', ar: 'Shade (Arabic)' },
                            'colors',
                          )}

                          {pairFields(
                            color.undertone,
                            (language, text) => setColorField(index, 'undertone', language, text),
                            { en: 'Undertone (English)', ar: 'Undertone (Arabic)' },
                            'colors',
                          )}

                          <Box sx={{ maxWidth: 240 }}>
                            {/* One box, because a shade code is an identifier a
                                shopper matches character for character rather
                                than a word in a language. */}
                            <CustomLabel label={'Code'} />
                            <TextField
                              size="small"
                              fullWidth
                              value={color.code}
                              disabled={!editable('colors')}
                              onChange={(event) => setColorCode(index, event.target.value)}
                            />
                          </Box>
                        </Stack>

                        {index < value.colors.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    ))}

                    {editable('colors') && (
                      <Box>
                        <CustomButton
                          text={'Add Color'}
                          variant={'outlined'}
                          startIcon={<AddIcon />}
                          height={38}
                          onClick={() =>
                            setValue((previous) => ({
                              ...previous,
                              colors: [
                                ...previous.colors,
                                {
                                  family: emptyPair(),
                                  shade: emptyPair(),
                                  code: '',
                                  undertone: emptyPair(),
                                },
                              ],
                            }))
                          }
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              <Box
                sx={{
                  border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <CustomText text={'Scent'} fontWeight={600} />
                  {reloadButton('appearance', 'Read the colour, scent and flavour off the pack again')}
                  {sectionButtons('scent')}
                  {clearButton('scent')}
                </Stack>

                <Stack gap={1.25} mt={1.5}>
                  {pairFields(
                    value.scent.family,
                    (language, text) => setScent('family', language, text),
                    { en: 'Family (English)', ar: 'Family (Arabic)' },
                    'scent',
                  )}

                  {pairFields(
                    value.scent.name,
                    (language, text) => setScent('name', language, text),
                    { en: 'Name (English)', ar: 'Name (Arabic)' },
                    'scent',
                  )}
                </Stack>
              </Box>

              <Box
                sx={{
                  border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <CustomText text={'Flavor'} fontWeight={600} />
                  {reloadButton('appearance', 'Read the colour, scent and flavour off the pack again')}
                  {sectionButtons('flavor')}
                  {clearButton('flavor')}
                </Stack>

                <Stack gap={1.25} mt={1.5}>
                  {pairFields(
                    value.flavor.family,
                    (language, text) => setFlavor('family', language, text),
                    { en: 'Family (English)', ar: 'Family (Arabic)' },
                    'flavor',
                  )}

                  {pairFields(
                    value.flavor.name,
                    (language, text) => setFlavor('name', language, text),
                    { en: 'Name (English)', ar: 'Name (Arabic)' },
                    'flavor',
                  )}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        }
      />
    </Box>
  )
}

export default memo(AttributesDetails)
